import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { Icon, Input, Select, TextArea } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";

import { Classification, Schema } from "../../../../../../api";

import {
  initialValues,
  validationSchema,
} from "./CreateClassificationPage.form";

import styles from "./CreateClassificationPage.module.scss";

const classificationCtrl = new Classification();
const schemaCtrl = new Schema();

function getClassificationsResponse(result) {
  const response =
    result?.classifications || result?.response || result || {};

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  return [];
}

export function CreateClassificationPage() {
  const navigate = useNavigate();

  const [schemas, setSchemas] = useState([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [serverError, setServerError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Clasificaciones que ya existen en el esquema elegido -- para
  // sugerir el siguiente "orden" disponible dentro de ese esquema
  // (igual que en CreateCategoryPage, pero acá compite por esquema en
  // vez de por categoría padre). Mientras la persona no toque "Orden"
  // a mano, se le sigue sugiriendo el que toca según lo que ya exista.
  const [schemaClassifications, setSchemaClassifications] = useState([]);
  const [orderTouchedByUser, setOrderTouchedByUser] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,

    onSubmit: async (values) => {
      try {
        setServerError(null);

        const classificationData = {
          id: values.id.trim().toUpperCase(),
          name: values.name.trim(),
          description: values.description.trim(),
          schema: values.schema,
          order: Number(values.order),
        };

        await classificationCtrl.createClassification(classificationData);

        navigate("/admin/catalog/classification", {
          state: {
            success: "Clasificación creada correctamente.",
          },
        });
      } catch (error) {
        console.error("Error al crear clasificación:", error);

        setServerError(
          error?.msg ||
            "No fue posible crear la clasificación. Intenta nuevamente."
        );

        if (error?.errors) {
          formik.setErrors(error.errors);
        }

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
  });

  useEffect(() => {
    async function loadSchemas() {
      try {
        setIsLoadingSchemas(true);
        setServerError(null);

        const result = await schemaCtrl.getSchemas(1, 1000, {});
        const response = result?.schemas || result?.response || result || {};

        const docs = Array.isArray(response.docs)
          ? response.docs
          : Array.isArray(response)
            ? response
            : [];

        const schemaOptions = docs.map((schema) => ({
          key: schema._id,
          value: schema._id,
          text: `${schema.id} - ${schema.name}`,
        }));

        setSchemas(schemaOptions);
      } catch (error) {
        console.error("Error al cargar esquemas:", error);

        setServerError(
          error?.msg ||
            "No fue posible cargar los esquemas disponibles."
        );
      } finally {
        setIsLoadingSchemas(false);
      }
    }

    loadSchemas();
  }, []);

  // Se recarga cada vez que la persona elige un esquema distinto, para
  // saber qué "orden" ya está en uso ahí y sugerir el siguiente.
  useEffect(() => {
    async function loadSchemaClassifications() {
      if (!formik.values.schema) {
        setSchemaClassifications([]);
        return;
      }

      try {
        const result = await classificationCtrl.getClassifications(1, 1000, {
          schema: formik.values.schema,
        });

        setSchemaClassifications(getClassificationsResponse(result));
      } catch (error) {
        console.error(
          "Error al cargar clasificaciones del esquema:",
          error
        );

        setSchemaClassifications([]);
      }
    }

    loadSchemaClassifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.schema]);

  const suggestedOrder = useMemo(() => {
    if (schemaClassifications.length === 0) {
      return 1;
    }

    const maxOrder = schemaClassifications.reduce(
      (max, item) => Math.max(max, Number(item.order) || 1),
      0
    );

    return maxOrder + 1;
  }, [schemaClassifications]);

  // Mientras la persona no haya tocado "Orden" a mano, se le sugiere
  // solo -- incluyendo la primera vez que elige un esquema.
  useEffect(() => {
    if (orderTouchedByUser) {
      return;
    }

    formik.setFieldValue("order", suggestedOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedOrder, orderTouchedByUser]);

  const getFieldError = (fieldName) => {
    const shouldShowError =
      (hasSubmitted || formik.touched[fieldName]) &&
      formik.errors[fieldName];

    return shouldShowError ? formik.errors[fieldName] : null;
  };

  const handleCancel = () => {
    if (formik.isSubmitting) {
      return;
    }

    navigate("/admin/catalog/classification");
  };

  const handleInputChange = (event, { name, value }) => {
    if (name === "order") {
      setOrderTouchedByUser(true);
    }

    formik.setFieldValue(name, value);

    if (hasSubmitted) {
      formik.validateField(name);
    }

    setServerError(null);
  };

  const handleUseSuggestedOrder = () => {
    setOrderTouchedByUser(false);
    formik.setFieldValue("order", suggestedOrder);
  };

  const handleInputBlur = (event) => {
    formik.handleBlur(event);

    if (hasSubmitted) {
      formik.validateField(event.target.name);
    }
  };

  const handleSchemaBlur = () => {
    formik.setFieldTouched("schema", true);

    if (hasSubmitted) {
      formik.validateField("schema");
    }
  };

  const handleCreate = async () => {
    setHasSubmitted(true);

    const touchedFields = {
      id: true,
      name: true,
      description: true,
      schema: true,
      order: true,
    };

    formik.setTouched(touchedFields, false);

    const validationErrors = await formik.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    formik.handleSubmit();
  };

  const isSaving = formik.isSubmitting;
  const isFormDisabled = isSaving || isLoadingSchemas;

  const showValidationError =
    hasSubmitted && Object.keys(formik.errors).length > 0;

  return (
    <section className={styles.CreateClassificationPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Volver al listado de clasificaciones"
          >
            <Icon name="arrow left" />
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Catálogo / Clasificaciones / Nueva clasificación
            </p>

            <h1>Nueva clasificación</h1>

            <p className={styles.Subtitle}>
              Crea un valor asociado a uno de los esquemas del catálogo.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancelar
          </button>

          <button
            type="button"
            className={styles.PublishButton}
            onClick={handleCreate}
            disabled={isFormDisabled}
          >
            {isSaving ? "Creando..." : "Crear clasificación"}
          </button>
        </div>
      </header>

      {showValidationError && (
        <div className={styles.ValidationMessage}>
          <Icon name="warning sign" />

          <div>
            <strong>Revisa la información de la clasificación.</strong>

            <span>
              Debes corregir los campos obligatorios antes de crear la
              clasificación.
            </span>
          </div>
        </div>
      )}

      {serverError && (
        <div className={styles.ServerErrorMessage}>
          <Icon name="warning sign" />
          <span>{serverError}</span>
        </div>
      )}

      <form
        className={styles.FormContent}
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <section className={styles.ClassificationBasicInformation}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información de la clasificación</h2>

              <p>
                Define el esquema padre y los datos del valor que deseas
                registrar.
              </p>
            </div>
          </header>

          <div className={styles.InfoMessage}>
            <span className={styles.InfoIcon}>i</span>

            <p>
              Ejemplo: para el esquema <strong>Tipo de producto</strong>,
              puedes registrar una clasificación llamada{" "}
              <strong>Bolsas</strong>.
            </p>
          </div>

          <div className={styles.FormGrid}>
            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="schema">
                Esquema <span>*</span>
              </label>

              <Select
                id="schema"
                name="schema"
                fluid
                search
                selection
                options={schemas}
                value={formik.values.schema}
                placeholder={
                  isLoadingSchemas
                    ? "Cargando esquemas..."
                    : "Selecciona un esquema"
                }
                error={Boolean(getFieldError("schema"))}
                loading={isLoadingSchemas}
                disabled={isFormDisabled}
                noResultsMessage="No se encontraron esquemas."
                onChange={handleInputChange}
                onBlur={handleSchemaBlur}
              />

              {getFieldError("schema") && (
                <span className={styles.FieldError}>
                  {getFieldError("schema")}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="id">
                Código de la clasificación <span>*</span>
              </label>

              <Input
                id="id"
                name="id"
                value={formik.values.id}
                placeholder="Ej. BOLSAS"
                error={Boolean(getFieldError("id"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              {getFieldError("id") && (
                <span className={styles.FieldError}>
                  {getFieldError("id")}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="order">
                Orden de visualización <span>*</span>
              </label>

              <Input
                id="order"
                name="order"
                type="number"
                min="1"
                value={formik.values.order}
                placeholder="Ej. 1"
                error={Boolean(getFieldError("order"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              {getFieldError("order") ? (
                <span className={styles.FieldError}>
                  {getFieldError("order")}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Siguiente disponible en este esquema: {suggestedOrder}.{" "}
                  {Number(formik.values.order) !== suggestedOrder && (
                    <button
                      type="button"
                      className={styles.UseSuggestedOrderButton}
                      onClick={handleUseSuggestedOrder}
                      disabled={isFormDisabled}
                    >
                      Usar {suggestedOrder}
                    </button>
                  )}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="name">
                Nombre de la clasificación <span>*</span>
              </label>

              <Input
                id="name"
                name="name"
                value={formik.values.name}
                placeholder="Ej. Bolsas"
                error={Boolean(getFieldError("name"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              {getFieldError("name") && (
                <span className={styles.FieldError}>
                  {getFieldError("name")}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <TextArea
                id="description"
                name="description"
                value={formik.values.description}
                placeholder="Ej. Productos que corresponden a la categoría de bolsas."
                rows={5}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isFormDisabled}
              />

              <div className={styles.TextAreaFooter}>
                {getFieldError("description") ? (
                  <span className={styles.FieldError}>
                    {getFieldError("description")}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formik.values.description.length}/500</span>
              </div>
            </div>
          </div>
        </section>
      </form>
    </section>
  );
}